import React from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import styles from "./About.module.css";
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom';
// Fallback components for ServicesList and ContactInfo
const FallbackServicesList = () => (
  <section className={styles.servicesSection}>
    <motion.h2
      initial={{ opacity: 0, x: -50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8 }}
    >
      Our Services
    </motion.h2>
    <motion.p
      initial={{ opacity: 0, x: -50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      Comprehensive photography and cinematography solutions for weddings and events.
    </motion.p>
  </section>
);
const MotionLink = motion(Link);
const FallbackContactInfo = () => (
  <section className={styles.contactSection}>
    <motion.h2
      initial={{ opacity: 0, x: -50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8 }}
    >
      Contact Us
    </motion.h2>
    <motion.p
      initial={{ opacity: 0, x: -50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      Email: info@studiomaamangala.com | Phone: +91 83288 03919
    </motion.p>
    <motion.p
      initial={{ opacity: 0, x: -50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.4 }}
    >
      Address: Jharpokharia, Odisha, India
    </motion.p>
  </section>
);

const ServicesList = typeof window !== "undefined" && window.ServicesList ? window.ServicesList : FallbackServicesList;
const ContactInfo = typeof window !== "undefined" && window.ContactInfo ? window.ContactInfo : FallbackContactInfo;

const About = () => {
  const { scrollYProgress } = useScroll();
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const springParallax = useSpring(parallaxY, { stiffness: 120, damping: 25 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7, type: "spring" } },
  };

  return (
    <motion.div
      className={styles.aboutPage}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
    >
      <Helmet>
        <title>About Studio Maamangala | Premier Wedding Storytelling</title>
        <meta
          name="description"
          content="Studio Maamangala, based in Jharpokharia, Odisha, delivers innovative wedding photography and cinematography with a passion for storytelling."
        />
        <meta
          name="keywords"
          content="Studio Maamangala, wedding photography Odisha, Jharpokharia, cinematic storytelling, professional photography"
        />
        <meta property="og:title" content="About Studio Maamangala" />
        <meta
          property="og:description"
          content="Discover our award-winning approach to crafting timeless wedding stories in Odisha."
        />
        <meta property="og:url" content="https://studiomaamangala.com/about" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://studiomaamangala.com/wp-content/uploads/2025/02/michael-soledad-jiOByhCw2jE-unsplash-scaled.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Studio Maamangala | Innovative Wedding Photography" />
        <meta name="twitter:description" content="Learn about our creative process and achievements in Jharpokharia, Odisha." />
        <meta
          name="twitter:image"
          content="https://studiomaamangala.com/wp-content/uploads/2025/02/michael-soledad-jiOByhCw2jE-unsplash-scaled.jpg"
        />
      </Helmet>

      {/* Hero Section */}
      <section className={styles.hero}>
        <motion.div
          className={styles.heroOverlay}
          style={{ y: springParallax }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.85 }}
          transition={{ duration: 2 }}
        />
        <motion.img
          src="https://studiomaamangala.com/wp-content/uploads/2025/02/michael-soledad-jiOByhCw2jE-unsplash-scaled.jpg"
          alt="Studio Maamangala Wedding Scene"
          className={styles.heroImage}
          style={{ y: springParallax }}
          initial={{ scale: 1.3 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.5, type: "spring" }}
        />
        <motion.div className={styles.heroContent} variants={containerVariants} initial="hidden" animate="visible">
          <motion.h1 variants={itemVariants}>Studio Maamangala</motion.h1>
          <motion.p variants={itemVariants}>
            Pioneering <strong>wedding photography</strong> and <strong>cinematography</strong> in Jharpokharia, Odisha.
          </motion.p>
          <motion.p variants={itemVariants} className={styles.subText}>
            We craft emotive, innovative visuals that immortalize your story.
          </motion.p>
       <MotionLink
  to="/contact"
  className={styles.ctaButton}
  variants={itemVariants}
  whileHover={{ scale: 1.15, boxShadow: "0 0 20px rgba(212, 160, 23, 0.6)" }}
  whileTap={{ scale: 0.85 }}
>
  Start Your Journey
</MotionLink>
        </motion.div>
      </section>

      {/* Journey Section */}
      <motion.section
        className={styles.journeySection}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.div className={styles.journeyContent}>
          <motion.h2 variants={itemVariants}>Our Legacy</motion.h2>
          <motion.p variants={itemVariants}>
            Since 2015, Studio Maamangala has redefined wedding storytelling in Odisha. With over <strong>350 weddings</strong> captured, our work blends artistry and technology, earning the <strong>Odisha Wedding Photography Award 2023</strong>.
          </motion.p>
          <motion.p variants={itemVariants}>
            Featured in leading publications, we are trusted to transform moments into cinematic legacies.
          </motion.p>
        </motion.div>
      </motion.section>

      {/* Process Section */}
      <motion.section
        className={styles.processSection}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.div className={styles.processContent}>
          <motion.h2 variants={itemVariants}>Our Process</motion.h2>
          <div className={styles.processGrid}>
            <motion.div
              className={styles.processCard}
              variants={itemVariants}
              whileHover={{ y: -15, rotate: 2, boxShadow: "0 15px 30px rgba(212, 160, 23, 0.4)" }}
            >
              <h3>Consultation</h3>
              <p>Personalized planning to understand your vision and needs.</p>
            </motion.div>
            <motion.div
              className={styles.processCard}
              variants={itemVariants}
              whileHover={{ y: -15, rotate: -2, boxShadow: "0 15px 30px rgba(212, 160, 23, 0.4)" }}
            >
              <h3>Shooting</h3>
              <p>Capturing every moment with precision and creativity.</p>
            </motion.div>
            <motion.div
              className={styles.processCard}
              variants={itemVariants}
              whileHover={{ y: -15, rotate: 2, boxShadow: "0 15px 30px rgba(212, 160, 23, 0.4)" }}
            >
              <h3>Editing</h3>
              <p>Crafting polished visuals with meticulous attention to detail.</p>
            </motion.div>
            <motion.div
              className={styles.processCard}
              variants={itemVariants}
              whileHover={{ y: -15, rotate: -2, boxShadow: "0 15px 30px rgba(212, 160, 23, 0.4)" }}
            >
              <h3>Delivery</h3>
              <p>Delivering timeless memories in stunning formats.</p>
            </motion.div>
          </div>
        </motion.div>
      </motion.section>

      {/* Achievements Section */}
      <motion.section
        className={styles.achievementsSection}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.div className={styles.achievementsContent}>
          <motion.h2 variants={itemVariants}>Our Achievements</motion.h2>
          <div className={styles.achievementsGrid}>
            <motion.div className={styles.achievementCard} variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Award-Winning</h3>
              <p>Odisha Wedding Photography Award 2023 for Innovation.</p>
            </motion.div>
            <motion.div className={styles.achievementCard} variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Trusted by 350+</h3>
              <p>Couples across Odisha choose us for their special day.</p>
            </motion.div>
            <motion.div className={styles.achievementCard} variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Media Featured</h3>
              <p>Showcased in top regional and national publications.</p>
            </motion.div>
            <motion.div className={styles.achievementCard} variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>10+ Years</h3>
              <p>Of excellence in wedding photography and cinematography.</p>
            </motion.div>
          </div>
        </motion.div>
      </motion.section>

      {/* Features Section */}
      <motion.section
        className={styles.featuresSection}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.div className={styles.featuresContent}>
          <motion.h2 variants={itemVariants}>Why Choose Us?</motion.h2>
          <div className={styles.featuresGrid}>
            <motion.div
              className={styles.featureCard}
              variants={itemVariants}
              whileHover={{ y: -10, rotate: 3 }}
            >
              <h3>Creative Vision</h3>
              <p>Blending artistry with technology for unique visuals.</p>
            </motion.div>
            <motion.div
              className={styles.featureCard}
              variants={itemVariants}
              whileHover={{ y: -10, rotate: -3 }}
            >
              <h3>Precision</h3>
              <p>Meticulous attention to every detail of your story.</p>
            </motion.div>
            <motion.div
              className={styles.featureCard}
              variants={itemVariants}
              whileHover={{ y: -10, rotate: 3 }}
            >
              <h3>Emotion</h3>
              <p>Capturing authentic moments that resonate forever.</p>
            </motion.div>
            <motion.div
              className={styles.featureCard}
              variants={itemVariants}
              whileHover={{ y: -10, rotate: -3 }}
            >
              <h3>Innovation</h3>
              <p>Using cutting-edge techniques to redefine storytelling.</p>
            </motion.div>
          </div>
        </motion.div>
      </motion.section>

      {/* Testimonial Section */}
      <motion.section
        className={styles.testimonialSection}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.div className={styles.testimonialContent}>
          <motion.h2 variants={itemVariants}>What Clients Say</motion.h2>
          <div className={styles.testimonialGrid}>
            <motion.div
              className={styles.testimonialCard}
              variants={itemVariants}
              whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(212, 160, 23, 0.3)" }}
            >
              <p>“Studio Maamangala turned our wedding into a cinematic masterpiece.”</p>
              <span>Client, 2024</span>
            </motion.div>
            <motion.div
              className={styles.testimonialCard}
              variants={itemVariants}
              whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(212, 160, 23, 0.3)" }}
            >
              <p>“Their innovative approach made our memories truly unforgettable.”</p>
              <span>Client, 2023</span>
            </motion.div>
            <motion.div
              className={styles.testimonialCard}
              variants={itemVariants}
              whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(212, 160, 23, 0.3)" }}
            >
              <p>“Professional, creative, and a pleasure to work with!”</p>
              <span>Client, 2022</span>
            </motion.div>
          </div>
        </motion.div>
      </motion.section>

      {/* Services Section */}
      <ServicesList />

      {/* Contact Section */}
      <ContactInfo />
    </motion.div>
  );
};

export default About;